import React, { useState, useEffect, Suspense, lazy } from 'react';
import axios from 'axios';
import CompanySettings from './CompanySettings';
import ServicesSettings from './ServicesSettings';
import AutomationSettings from './AutomationSettings';
import AssignmentGroupsSettings from './AssignmentGroupsSettings';
import CurrencySettings from './CurrencySettings';
import EmailProfilesSettings from './EmailProfilesSettings';
import EmailTemplatesSettings from './EmailTemplatesSettings';
import EmailLogsView from './EmailLogsView';
import PermissionsSettings from './PermissionsSettings';
import BulkEmailSettings from './BulkEmailSettings';
import PipelineStagesSettings from './PipelineStagesSettings';
import ReminderSettings from './ReminderSettings';
import CalendarSettings from './CalendarSettings';
import GoogleAdsSettings from './GoogleAdsSettings';
import MetaAdsSettings from './MetaAdsSettings';
import SocialMediaSettings from './SocialMediaSettings';
import TrashSettingsView from './TrashSettingsView';
import ModulesSettings from './ModulesSettings';
import { VideoCoursesManager } from '../video-courses';
import { hasPermission } from '../../utils/permissions';
import { TabNavigation, Button, useNotification } from '../common'; // Import useNotification
import useModuleSettings from '../../hooks/useModuleSettings';

const Settings = () => {
  const { showNotification } = useNotification(); // Initialize useNotification
  const { moduleSettings, loading: moduleLoading } = useModuleSettings(); // Add module settings hook
  // Initial tab state (will be properly set after permissions check in useEffect)
  const [activeTab, setActiveTab] = useState('company');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [companyId, setCompanyId] = useState(null); // State for company ID for webhook URL
  const [companySettings, setCompanySettings] = useState({}); // State for general company settings
  
  // Fetch current user for authentication purposes
  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.get('/api/auth/me', config);
      setCurrentUser(res.data);
    } catch (err) {
      console.error('Error fetching current user:', err);
    }
  };
  

  // Separate useEffect for fetching current user
  useEffect(() => {
    fetchCurrentUser();
  }, []); // Run only once on mount

  // Separate useEffect for fetching data that depends on user or activeTab
  useEffect(() => {
    // Don't fetch other data until we have the current user
    if (!currentUser) return;

    // Get company ID from user object (more reliable than localStorage)
    // Removed unnecessary try block here
    if (currentUser.company_id) {
      setCompanyId(currentUser.company_id);
    } else {
      console.error('Company ID not found in currentUser object for webhook');
    }

    // Fetch general company settings
    const fetchCompanySettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { 'x-auth-token': token } };
        const res = await axios.get('/api/settings/company', config);
        setCompanySettings(res.data || {}); // Store fetched settings
        console.log('Fetched company settings:', res.data);
      } catch (err) {
        console.error('Error fetching company settings:', err);
        // Optionally show an error notification
        // showNotification('Failed to load company settings.', 'error');
      }
    };

    fetchCompanySettings(); // Fetch general settings on mount/tab change

    // Reset loading state (consider moving setLoading(true) to start of fetches)
    setLoading(false);

  }, [currentUser, activeTab]); // Rerun when currentUser or activeTab changes
  
  // Construct Webhook URL
  const webhookUrl = companyId
    ? `${window.location.origin}/api/leads/webhook/elementor?companyId=${companyId}`
    : 'Loading Company ID...';

  // Copy to clipboard function for webhook
  const copyWebhookToClipboard = () => {
    if (companyId) {
      navigator.clipboard.writeText(webhookUrl)
        .then(() => {
          showNotification('Webhook URL copied to clipboard!', 'success');
        })
        .catch(err => {
          console.error('Failed to copy webhook URL: ', err);
          showNotification('Failed to copy URL. Please copy manually.', 'error');
        });
    }
  };
  
  // Helper function to get tab icon based on id
  const getTabIcon = (id) => {
    switch (id) {
      case 'company':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm3 1h6v4H7V5zm8 8v-1.5a1.5 1.5 0 00-1.5-1.5h-9A1.5 1.5 0 003 11.5V13h1v-1.5a.5.5 0 01.5-.5h9a.5.5 0 01.5.5V13h1z" clipRule="evenodd" />
          </svg>
        );
      case 'modules':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" />
          </svg>
        );
      case 'currencies':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
          </svg>
        );
      case 'pipeline':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" />
          </svg>
        );
          
      case 'courses':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
          </svg>
        );
      case 'videoCourses':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
            <path d="M14 6a2 2 0 012-2h2a2 2 0 012 2v8a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z" />
          </svg>
        );
      case 'emailProfiles':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
          </svg>
        );
      case 'emailTemplates':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
            <path d="M7 2h5a1 1 0 010 2H7a1 1 0 110-2zm3 6a1 1 0 100 2h5a1 1 0 100-2h-5zm-1 4a1 1 0 10-2 0 1 1 0 002 0zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" />
          </svg>
        );
      case 'emailLogs':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
          </svg>
        );
      case 'permissions':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
        );
      case 'webhooks':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
          </svg>
        );
      case 'automation':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
          </svg>
        );
      case 'bulkEmail':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
            <path d="M13.74 4.23c-.84-1-1.57-1.39-2.4-1.68C10 2.11 8.89 2 8 2H2v20h6c.64 0 1.83-.16 2.72-.46 1.12-.39 1.96-1 2.63-1.78.75-.86 1.21-1.95 1.46-3.23.16-.83.19-1.57.19-2.58V9.82c0-.98-.05-1.71-.26-2.59-.28-1.11-.79-2.17-1.5-3M11.84 17c-.45.57-1.17.93-2.34.93H4V4h3.5c1.05 0 1.97.11 2.93.51.86.35 1.36 1 1.77 1.67.42.67.7 1.56.85 2.29.11.55.15 1.4.15 2.24v2.32c0 .94-.04 1.67-.21 2.26-.24.85-.7 1.36-1.15 1.71M20 13h-4v2h4v2l3-3-3-3v2Z"/>
          </svg>
        );
      case 'googleAds':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 8.91l-5.66 9.8l-.34-.2c-.81-.5-1.07-1.53-.58-2.34L10.5 7.74C11 6.92 12.04 6.66 12.84 7.16l.34.2l-1.18 1.55z"/>
            <path d="M13.34 7.36L12.16 8.9l5.66 9.81c.13.22.3.42.5.58a2.04 2.04 0 0 0 2.76-.58l5.1-8.86c.5-.81.24-1.85-.58-2.34l-11.6-6.71a2.04 2.04 0 0 0-2.76.58l-.34.6l1.84 1.06l.6-1.04l.01-.02z"/>
            <path d="M7.84 17.16l4.34 2.5l.34-.59l-4.72-8.2l-5.97 10.3c-.3.58-.3 1.2-.01 1.8c.28.59.83 1.01 1.48 1.19c.19.04.38.05.57.04c.66-.04 1.31-.37 1.7-1.02l2.27-3.93v-2.1z"/>
          </svg>
        );
      case 'metaAds':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.04c-5.5 0-10 4.49-10 10.02c0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89c1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.53-4.5-10.02-10-10.02z"/>
          </svg>
        );
      case 'socialMedia':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
            <path d="M14.94 5.19A4.38 4.38 0 0 0 16 2a4.44 4.44 0 0 0-3.17 1.33A4.15 4.15 0 0 0 11.78 6a3.69 3.69 0 0 0 3.16-2.81zM24 12a12 12 0 1 1-12-12 12 12 0 0 1 12 12zm-5.01-4.5A2.5 2.5 0 1 0 16.5 10a2.5 2.5 0 0 0 2.49-2.5zM7.5 7.5A2.5 2.5 0 1 0 10 10a2.5 2.5 0 0 0-2.5-2.5zm9 9A2.5 2.5 0 1 0 19 19a2.5 2.5 0 0 0-2.5-2.5zm-9 0A2.5 2.5 0 1 0 10 19a2.5 2.5 0 0 0-2.5-2.5z"/>
          </svg>
        );
      case 'reminders':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
        );
      case 'calendar':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
          </svg>
        );
      case 'trash':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      default:
        return null;
    }
  };

  // Define base tabs array
  const baseTabs = [
    {
      id: 'company',
      label: 'Company',
      permission: { section: 'settings', action: 'company' }
    },
    {
      id: 'modules',
      label: 'Modules',
      permission: { section: 'settings', action: 'company' }
    },
    {
      id: 'currencies',
      label: 'Currencies',
      permission: { section: 'settings', action: 'currencies' }
    },
    {
      id: 'pipeline',
      label: 'Pipeline Stages',
      permission: { section: 'settings', action: 'pipelineStages' }
    },
    {
      id: 'courses',
      label: 'Services',
      permission: { section: 'settings', action: 'courses' }
    },
    {
      id: 'videoCourses',
      label: 'Video Courses',
      permission: { section: 'videos', action: 'create' },
      moduleRequired: 'videoCourses' // Add module requirement
    },
    {
      id: 'emailProfiles',
      label: 'Email Profiles',
      permission: { section: 'settings', action: 'emailProfiles' }
    },
    {
      id: 'emailTemplates',
      label: 'Email Templates',
      permission: { section: 'settings', action: 'emailTemplates' }
    },
    {
      id: 'emailLogs',
      label: 'Email Logs',
      permission: { section: 'settings', action: 'emailLogs' }
    },
    {
      id: 'reminders',
      label: 'Reminders',
      permission: { section: 'settings', action: 'reminders' }
    },
    {
      id: 'calendar',
      label: 'Booking',
      permission: { section: 'calendar', action: 'create' }
    },
    {
      id: 'permissions',
      label: 'Permissions',
      permission: { section: 'settings', action: 'permissions' }
    },
    {
      id: 'webhooks',
      label: 'Webhooks',
      permission: { section: 'settings', action: 'integrations' }
    },
    {
      id: 'googleAds',
      label: 'Google Ads',
      permission: { section: 'settings', action: 'integrations' }
    },
    {
      id: 'metaAds',
      label: 'Meta Ads',
      permission: { section: 'settings', action: 'integrations' }
    },
    {
      id: 'socialMedia',
      label: 'Social Media',
      permission: { section: 'settings', action: 'socialMedia' }
    },
    {
      id: 'automation',
      label: 'Automation',
      permission: { section: 'settings', action: 'automation' }
    },
    {
      id: 'trash',
      label: 'Trash',
      permission: { section: 'settings', action: 'company' }
    },
    {
      id: 'bulkEmail',
      label: 'Amazon SES',
      permission: { section: 'settings', action: 'permissions' }
    }
  ];

  // Filter tabs based on module settings
  const tabs = baseTabs.filter(tab => {
    // If tab requires a module, check if that module is enabled
    if (tab.moduleRequired) {
      return moduleSettings[tab.moduleRequired] === true;
    }
    // Otherwise, include the tab
    return true;
  });

  // Set initial active tab based on permissions, URL params, and localStorage
  useEffect(() => {
    if (!currentUser) return;

    // First try to get tab from URL
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    
    // Then try localStorage
    const savedTab = localStorage.getItem('settingsActiveTab');
    
    // Determine preferred tab order (URL > localStorage > current > default)
    const preferredTabId = tabParam || savedTab || activeTab;
    
    // Check if the preferred tab is accessible
    const preferredTabDefinition = tabs.find(tab => tab.id === preferredTabId);
    const isPreferredTabAccessible = preferredTabDefinition && (
      !preferredTabDefinition.permission ||
      hasPermission(preferredTabDefinition.permission.section, preferredTabDefinition.permission.action)
    );
    
    if (isPreferredTabAccessible) {
      // If preferred tab is accessible, use it
      setActiveTab(preferredTabId);
      
      // Update URL if different from current param
      if (preferredTabId !== tabParam) {
        const url = new URL(window.location);
        url.searchParams.set('tab', preferredTabId);
        window.history.pushState({}, '', url);
      }
    } else {
      // Find the first accessible tab
      const firstAccessibleTab = tabs.find(tab =>
        !tab.permission || hasPermission(tab.permission.section, tab.permission.action)
      );

      if (firstAccessibleTab && firstAccessibleTab.id) {
        setActiveTab(firstAccessibleTab.id);
        localStorage.setItem('settingsActiveTab', firstAccessibleTab.id);
        
        // Update URL
        const url = new URL(window.location);
        url.searchParams.set('tab', firstAccessibleTab.id);
        window.history.pushState({}, '', url);
      }
    }

  }, [currentUser, tabs]); // Run when user data changes



  // Add console logs for debugging state
  console.log('[Settings Component Render] currentUser:', currentUser);
  console.log('[Settings Component Render] companySettings:', companySettings);
  console.log('[Settings Component Render] companySettings.webhook_secret:', companySettings?.webhook_secret);
  if (currentUser) {
    console.log('[Settings Component Render] currentUser.role_name:', currentUser.role_name);
    console.log('[Settings Component Render] Role check passes:', ['admin', 'superuser'].includes(currentUser.role_name));
  }
  
  return (
    <div className="bg-white rounded-lg shadow-md">
      {/* Using shared TabNavigation component */}
      <TabNavigation 
        tabs={tabs.map(tab => ({
          ...tab,
          icon: getTabIcon(tab.id)
        }))}
        activeTab={activeTab}
        onTabChange={(tabId) => {
          setActiveTab(tabId);
          // Save to localStorage for persistence
          localStorage.setItem('settingsActiveTab', tabId);
          // Update URL without reloading the page
          const url = new URL(window.location);
          url.searchParams.set('tab', tabId);
          window.history.pushState({}, '', url);
        }}
      />
      
      <div className="p-6">
        {/* Company Settings Tab */}
        {activeTab === 'company' && hasPermission('settings', 'company') && (
          <CompanySettings />
        )}
        
        {/* Modules Settings Tab */}
        {activeTab === 'modules' && hasPermission('settings', 'company') && (
          <ModulesSettings />
        )}
        
        {/* Currency Settings Tab */}
        {activeTab === 'currencies' && hasPermission('settings', 'currencies') && (
          <CurrencySettings />
        )}
        
        {/* Pipeline Stages Tab */}
        {activeTab === 'pipeline' && hasPermission('settings', 'pipelineStages') && (
          <PipelineStagesSettings />
        )}
        
        {/* Services Settings Tab */}
        {activeTab === 'courses' && hasPermission('settings', 'courses') && (
          <ServicesSettings />
        )}
        
        {/* Video Courses Tab */}
        {activeTab === 'videoCourses' && hasPermission('videos', 'create') && moduleSettings.videoCourses && (
          <VideoCoursesManager />
        )}
        
        {/* Email Profiles Tab */}
        {activeTab === 'emailProfiles' && hasPermission('settings', 'emailProfiles') && (
          <EmailProfilesSettings />
        )}
        
        {/* Email Templates Tab */}
        {activeTab === 'emailTemplates' && hasPermission('settings', 'emailTemplates') && (
          <EmailTemplatesSettings />
        )}
        
        {/* Email Logs Tab */}
        {activeTab === 'emailLogs' && hasPermission('settings', 'emailLogs') && (
          <EmailLogsView />
        )}
        
        {/* Reminders Tab */}
        {activeTab === 'reminders' && hasPermission('settings', 'reminders') && (
          <ReminderSettings />
        )}
        
        {/* Calendar Booking Tab */}
        {activeTab === 'calendar' && hasPermission('calendar', 'create') && (
          <CalendarSettings />
        )}
        
        
        {/* Permissions Tab */}
        {activeTab === 'permissions' && hasPermission('settings', 'permissions') && (
           <PermissionsSettings />
        )}
        
        {/* Bulk Email Tab */}
        {activeTab === 'bulkEmail' && hasPermission('settings', 'permissions') && (
          <BulkEmailSettings />
        )}
        
        {/* Google Ads Tab */}
        {activeTab === 'googleAds' && hasPermission('settings', 'integrations') && (
          <GoogleAdsSettings />
        )}
        
        {/* Meta Ads Tab */}
        {activeTab === 'metaAds' && hasPermission('settings', 'integrations') && (
          <MetaAdsSettings />
        )}
        
        {/* Social Media Tab */}
        {activeTab === 'socialMedia' && hasPermission('settings', 'socialMedia') && (
          <SocialMediaSettings />
        )}
        
        {/* Trash Tab */}
        {activeTab === 'trash' && hasPermission('settings', 'company') && (
          <TrashSettingsView />
        )}

        {/* Automation Tab */}
        {activeTab === 'automation' && hasPermission('settings', 'automation') && (
          <div>
            <AutomationSettings />
            <div className="mt-8 border-t border-gray-200 pt-6">
              <AssignmentGroupsSettings />
            </div>
          </div>
        )}
        
        {/* Webhooks Tab */}
        {activeTab === 'webhooks' && hasPermission('settings', 'integrations') && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Webhook Configuration</h3>
            
            {/* Display error or success messages */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
                {error}
              </div>
            )}
            
            {success && (
              <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded">
                {success}
              </div>
            )}
            
            {/* Company-Specific Webhook URL Section */}
            <div className="bg-gray-50 p-4 rounded-md mb-6">
              <h4 className="font-medium text-gray-800 mb-3">Webhook URL for Lead Integration</h4>
              <p className="text-sm text-gray-500 mb-2">
                Use this URL in external systems (like WordPress Elementor or Make.com) to send new leads directly to your company account in the CRM.
              </p>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  readOnly
                  value={webhookUrl}
                  className="flex-grow p-2 border border-gray-300 rounded-md bg-gray-50 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={copyWebhookToClipboard}
                  disabled={!companyId}
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={companyId ? "Copy URL" : "Company ID not loaded"}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
              {!companyId && (
                <p className="text-xs text-yellow-600 mt-1">Loading your company ID...</p>
              )}
              {/* Instructions Box */}
              <div className="bg-blue-50 p-4 rounded-md border border-blue-100 mt-4">
                <h5 className="text-sm font-medium text-blue-800 mb-2">Setup Instructions</h5>
                <ol className="list-decimal pl-5 text-sm text-blue-700 space-y-1">
                  <li>In your external system (e.g., Elementor form settings, Make.com scenario), find the webhook action.</li>
                  <li>Paste the unique Webhook URL provided above into the URL field.</li>
                  <li>Ensure the request method is set to POST.</li>
                  <li>Map your form fields or data payload to include at minimum: <code className="bg-blue-100 px-1 rounded">name</code>, <code className="bg-blue-100 px-1 rounded">email</code>, and <code className="bg-blue-100 px-1 rounded">phone</code>.</li>
                  <li>Additional fields like <code className="bg-blue-100 px-1 rounded">source</code>, <code className="bg-blue-100 px-1 rounded">message</code>, or custom fields can also be sent.</li>
                  <li><span className="font-medium">Note:</span> This URL includes your unique Company ID and does not require a separate security key.</li>
                </ol>
              </div>
            </div>

            {/* Webhook Secret Display Section (Only for Admin/Superuser) */}
            {/* Corrected role check to use currentUser.role_name */}
            {currentUser && ['admin', 'superuser'].includes(currentUser.role_name) && companySettings.webhook_secret && (
              <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200 mb-6">
                <h4 className="font-medium text-gray-800 mb-3">Webhook Secret Key</h4>
                <p className="text-sm text-gray-600 mb-2">
                  Use this secret key in systems like Make.com to authenticate webhook requests sent to your CRM. Add it as an HTTP header named <code className="bg-yellow-100 px-1 rounded text-yellow-800">X-Webhook-Secret</code>.
                </p>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    readOnly
                    value={companySettings.webhook_secret}
                    className="flex-grow p-2 border border-gray-300 rounded-md bg-gray-100 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(companySettings.webhook_secret)
                        .then(() => showNotification('Webhook Secret copied!', 'success'))
                        .catch(err => showNotification('Failed to copy secret.', 'error'));
                    }}
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                    title="Copy Secret"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                  {/* Placeholder for Regenerate button - requires backend endpoint */}
                  {/*
                  <button
                    type="button"
                    // onClick={handleRegenerateSecret} // Needs implementation
                    className="px-3 py-2 bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-400 text-xs"
                    title="Regenerate Secret (Requires confirmation)"
                  >
                    Regenerate
                  </button>
                  */}
                </div>
                 <p className="text-xs text-yellow-700 mt-2">
                  Treat this key like a password. If you suspect it's compromised, you should regenerate it (functionality coming soon).
                </p>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;